import React, { useMemo, useState } from 'react';

/**
 * PolicySelector — reusable modal for selecting policies grouped by LOB.
 *
 * Props:
 *   policies      — array of policy objects (pre-filtered to client)
 *   onContinue      — (selectedPolicies) => void
 *   onClose         — () => void
 *   title           — string (default "Select Policies for Invoice")
 *   initialSelected — array of policy ids to pre-check (for Back button round-trip)
 */
export default function PolicySelector({ policies = [], onContinue, onClose, title = 'Select Policies for Invoice', initialSelected = [] }) {
  const [selected, setSelected] = useState(() => new Set(initialSelected));

  // Group by LOB
  const groups = useMemo(() => {
    const map = {};
    policies.forEach(p => {
      const lob = p.LOB || 'Other';
      if (!map[lob]) map[lob] = [];
      map[lob].push(p);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [policies]);

  function togglePolicy(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleGroup(lobPolicies) {
    const ids = lobPolicies.map(p => p.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => { if (allSelected) next.delete(id); else next.add(id); });
      return next;
    });
  }

  const runningTotal = useMemo(() => {
    return policies
      .filter(p => selected.has(p.id))
      .reduce((sum, p) => sum + (Number(p.Premium) || 0), 0);
  }, [policies, selected]);

  const selectedPolicies = policies.filter(p => selected.has(p.id));

  return (
    <div className="FIG-claude-backdrop" onClick={onClose}>
      <div className="FIG-claude-panel" style={{ maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <button className="FIG-claude-close" onClick={onClose}>✕</button>

        {/* Title */}
        <div className="FIG-claude-title" style={{ marginBottom: 4 }}>{title.toUpperCase()}</div>
        <div className="FIG-claude-divider" />

        {/* Policy list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {groups.length === 0 && (
            <div style={{ color: '#666', fontFamily: "'Share Tech Mono', monospace", fontSize: 12, textAlign: 'center', padding: 24 }}>
              No policies found.
            </div>
          )}
          {groups.map(([lob, lobPolicies]) => {
            const allChecked = lobPolicies.every(p => selected.has(p.id));
            const someChecked = lobPolicies.some(p => selected.has(p.id));
            return (
              <div key={lob} style={{ marginBottom: 16 }}>
                {/* LOB header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                  borderBottom: '1px solid #2a2a2a', paddingBottom: 6,
                }}>
                  <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => toggleGroup(lobPolicies)}
                    style={{ accentColor: '#d4a657', width: 14, height: 14, cursor: 'pointer' }} />
                  <span style={{
                    fontFamily: "'Share Tech Mono', monospace", fontSize: 11, fontWeight: 700,
                    color: '#d4a657', letterSpacing: '0.8px', textTransform: 'uppercase',
                  }}>{lob}</span>
                  <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 10, color: '#555' }}>
                    ({lobPolicies.length})
                  </span>
                </div>
                {/* Policy rows */}
                {lobPolicies.map(p => {
                  const carrierShort = (p.Carrier || 'Unknown').split(/[\s,]+/).slice(0, 2).join(' ');
                  const st = (p.Status || '').toLowerCase();
                  const isQuoted = st !== 'active' && st !== 'bound';
                  const number = p.PolicyNumber || p.QuoteNumber || '';
                  const term = [p.EffectiveDate, p.ExpirationDate].filter(Boolean).join(' – ');
                  return (
                    <label key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0 5px 22px',
                      cursor: 'pointer', borderRadius: 4,
                      background: selected.has(p.id) ? 'rgba(212,166,87,0.08)' : 'transparent',
                    }}>
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => togglePolicy(p.id)}
                        style={{ accentColor: '#d4a657', width: 13, height: 13, cursor: 'pointer' }} />
                      <span style={{
                        background: '#1a1a2e', border: '1px solid #333', borderRadius: 3,
                        padding: '1px 6px', fontSize: 9, fontWeight: 700, color: '#aaa',
                        fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.5px',
                        minWidth: 36, textAlign: 'center',
                      }}>{(p.LOB || '??').slice(0, 5)}</span>
                      {isQuoted && (
                        <span style={{
                          border: '1px solid #d4a657', borderRadius: 3,
                          padding: '1px 6px', fontSize: 9, fontWeight: 700, color: '#d4a657',
                          fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.5px',
                        }}>QUOTED</span>
                      )}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: '#ccc', fontFamily: "'Share Tech Mono', monospace" }}>
                          {carrierShort}
                        </span>
                        {(number || term) && (
                          <span style={{ display: 'block', fontSize: 10, color: '#777', fontFamily: "'Share Tech Mono', monospace" }}>
                            {number && <span>#{number}</span>}
                            {number && term ? ' ' : ''}
                            {term}
                          </span>
                        )}
                      </span>
                      <span style={{
                        fontSize: 12, color: '#d4a657', fontFamily: "'Share Tech Mono', monospace", fontWeight: 600,
                      }}>
                        ${(Number(p.Premium) || 0).toLocaleString()}
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #2a2a2a', padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 12, color: '#888' }}>
            {selected.size} selected &nbsp;&middot;&nbsp;
            <span style={{ color: '#d4a657', fontWeight: 700 }}>${runningTotal.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              background: '#1a1a1a', border: '1px solid #333', borderRadius: 4,
              color: '#aaa', fontWeight: 600, fontSize: 11, padding: '8px 16px', cursor: 'pointer',
            }}>CANCEL</button>
            <button disabled={selected.size === 0} onClick={() => onContinue(selectedPolicies)} style={{
              background: selected.size > 0 ? '#d4a657' : '#333',
              border: '1px solid ' + (selected.size > 0 ? '#d4a657' : '#444'),
              borderRadius: 4, color: selected.size > 0 ? '#0f0f0f' : '#666',
              fontWeight: 700, fontSize: 11, padding: '8px 20px',
              cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s, color 0.15s',
            }}>CONTINUE</button>
          </div>
        </div>
      </div>
    </div>
  );
}
